import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { IconPlus, IconRoute, IconSearch } from "@tabler/icons-react";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Group,
  Input,
  PaginadorTabla,
  Stack,
  Switch,
  TablaSkeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@/components/ui";
import { useServerPagination } from "@/lib/hooks/use-server-pagination";
import { formatFechaHora } from "@/shared/format/fecha";
import { useCambiarActivoFlujo, useCatalogoNotificaciones, useFlujos } from "../../hooks/use-notificaciones";
import { CrearFlujoDialog } from "./CrearFlujoDialog";

/** Listado paginado de flujos (P42) con activar/desactivar en línea. */
export function FlujosTab() {
  const p = useServerPagination<{ busqueda: string }>({ busqueda: "" });
  const params = useMemo(
    () => ({ busqueda: p.filtrosDebounced.busqueda.trim() || undefined, page: p.page, pageSize: p.pageSize }),
    [p.filtrosDebounced, p.page, p.pageSize],
  );
  const { data, isLoading } = useFlujos(params);
  const cambiarActivo = useCambiarActivoFlujo();
  const { data: catalogo } = useCatalogoNotificaciones();
  const nombreEvento = useMemo(() => new Map(catalogo.eventos.map((e) => [e.codigo, e.nombre])), [catalogo]);
  const [crear, setCrear] = useState(false);
  const flujos = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <Card padding="lg">
      <Stack gap="md">
        <Group justify="space-between">
          <Input
            placeholder="Buscar flujo…"
            leftSection={<IconSearch size={14} />}
            value={p.filtros.busqueda}
            onChange={(e) => p.patchFiltros({ busqueda: e.target.value })}
            wrapperClassName="w-full max-w-sm"
          />
          <Button leftSection={<IconPlus size={14} />} onClick={() => setCrear(true)}>
            Nuevo flujo
          </Button>
        </Group>

        {!isLoading && total === 0 ? (
          <EmptyState
            icon={IconRoute}
            title={params.busqueda ? "Sin resultados" : "Aún no hay flujos"}
            description={params.busqueda ? "Prueba con otro nombre." : "Crea un flujo para ligar eventos del portal con notificaciones."}
            tone={params.busqueda ? "filter" : "default"}
          />
        ) : (
          <>
            <Table>
              <Thead>
                <Tr>
                  <Th>Flujo</Th>
                  <Th>Eventos</Th>
                  <Th align="center">Notificaciones</Th>
                  <Th>Versión</Th>
                  <Th>Actualizado</Th>
                  <Th align="center">Activo</Th>
                </Tr>
              </Thead>
              {isLoading ? (
                <TablaSkeleton dentroDeTabla columnas={[{ lineas: 2 }, {}, { align: "center" }, {}, {}, { align: "center" }]} filas={p.pageSize} />
              ) : (
                <Tbody>
                  {flujos.map((f) => (
                    <Tr key={f.id}>
                      <Td>
                        <Link to="/admin/notificaciones/$id" params={{ id: f.id }} className="font-medium text-multiva-700 hover:underline dark:text-multiva-300">
                          {f.nombre}
                        </Link>
                        {f.descripcion ? <Text size="xs" c="dimmed" truncate>{f.descripcion}</Text> : null}
                      </Td>
                      <Td>
                        <Text size="sm">{f.eventos.length === 0 ? "—" : f.eventos.slice(0, 2).map((c) => nombreEvento.get(c) ?? c).join(", ")}</Text>
                        {f.eventos.length > 2 ? <Text size="xs" c="dimmed">+{f.eventos.length - 2} más</Text> : null}
                      </Td>
                      <Td align="center">{f.notificaciones}</Td>
                      <Td>
                        <Group gap="xs">
                          {f.versionVigente ? (
                            <Badge color="exito">v{f.versionVigente}</Badge>
                          ) : (
                            <Badge color="slate">Sin publicar</Badge>
                          )}
                          {f.versionVigente && f.borradorConCambios ? <Badge color="advertencia">Cambios sin publicar</Badge> : null}
                        </Group>
                      </Td>
                      <Td><Text size="sm">{formatFechaHora(f.actualizadoAt)}</Text></Td>
                      <Td align="center">
                        <Switch
                          checked={f.activo}
                          aria-label={f.activo ? "Desactivar flujo" : "Activar flujo"}
                          disabled={cambiarActivo.isPending}
                          onCheckedChange={(activo) => cambiarActivo.mutate({ id: f.id, activo })}
                        />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              )}
            </Table>
            <PaginadorTabla cargando={isLoading} total={total} page={p.page} pageSize={p.pageSize} onPageChange={p.setPage} sustantivo="flujos" />
          </>
        )}
      </Stack>
      <CrearFlujoDialog abierto={crear} onCerrar={() => setCrear(false)} />
    </Card>
  );
}
